import React, { useState } from 'react';
import { useAppContext } from '../../context/AppContext';
import { Room } from 'shared/types';
import { i18n } from '../../i18n/i18n';
import { showToast } from '../kid/Toast';
import { PictureTile } from '../kid/KidUI';
import { createRoom as apiCreateRoom, uploadRoomImageApi, removeRoomImageApi } from '../../services/apiService';
import { EditorModal, useCloseGuard } from './EditorModal';

/** Admin → Rooms: rooms list, room form and delete (split out of AdminPanel.tsx, 2026-10-12). */
const RoomsTab: React.FC = () => {
  const { current_user, current_family, refreshFamilyData, updateRoom, deleteRoom } = useAppContext() as any;
  const [showAddRoomModal, setShowAddRoomModal] = useState(false);
  const [removeRoomModal, setRemoveRoomModal] = useState<{ isOpen: boolean; roomId: string; roomName: string; error?: string; isDeleting?: boolean }>({
    isOpen: false,
    roomId: '',
    roomName: ''
  });
  const [tempRoomData, setTempRoomData] = useState<Partial<Room>>({});
  // Room picture (2026-10-13): a new photo waiting to be saved, or "remove the current one".
  const [roomFile, setRoomFile] = useState<File | null>(null);
  const [roomPreview, setRoomPreview] = useState<string | null>(null);
  const [removePicture, setRemovePicture] = useState(false);
  const [saving, setSaving] = useState(false);

  const closeForm = () => {
    setShowAddRoomModal(false);
    setTempRoomData({});
    setRoomFile(null);
    setRoomPreview(null);
    setRemovePicture(false);
  };

  /** After the room itself is saved: upload a new picture or remove the old one. */
  const savePicture = async (roomId: string) => {
    if (roomFile) await uploadRoomImageApi(roomId, roomFile);
    else if (removePicture && tempRoomData.image_url) await removeRoomImageApi(roomId);
  };

  const handleCreateRoom = async () => {
    try {
      if (!tempRoomData.name || tempRoomData.name.trim() === '') {
        showToast({ message: i18n.t('admin_panel.err_room_name'), tone: 'error' });
        return;
      }

      setSaving(true);
      const room = await apiCreateRoom({
        name: tempRoomData.name.trim(),
        original_name: tempRoomData.name.trim()
      });
      try {
        if (room?.id) await savePicture(room.id);
      } catch {
        showToast({ message: i18n.t('admin_panel.room_picture_failed'), tone: 'error' });
      }
      await refreshFamilyData();
      closeForm();
    } catch (error) {
      console.error('Failed to create room:', error);
      showToast({ message: i18n.t('admin_panel.err_room_create'), tone: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateRoom = async () => {
    try {
      if (!tempRoomData.name || tempRoomData.name.trim() === '') {
        showToast({ message: i18n.t('admin_panel.err_room_name'), tone: 'error' });
        return;
      }

      if (!tempRoomData.id) {
        showToast({ message: i18n.t('admin_panel.err_generic_missing'), tone: 'error' });
        return;
      }

      setSaving(true);
      await updateRoom(tempRoomData.id, {
        name: tempRoomData.name.trim(),
        original_name: tempRoomData.name.trim()
      });
      try {
        await savePicture(tempRoomData.id);
      } catch {
        showToast({ message: i18n.t('admin_panel.room_picture_failed'), tone: 'error' });
      }
      await refreshFamilyData();
      closeForm();
    } catch (error) {
      console.error('Failed to update room:', error);
      showToast({ message: i18n.t('admin_panel.err_room_update'), tone: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDeleteRoom = async () => {
    if (!removeRoomModal.roomId) return;
    setRemoveRoomModal(prev => ({ ...prev, isDeleting: true, error: undefined }));
    try {
      await deleteRoom(removeRoomModal.roomId);
      await refreshFamilyData();
      setRemoveRoomModal({ isOpen: false, roomId: '', roomName: '' });
      setShowAddRoomModal(false);
      setTempRoomData({});
    } catch (error) {
      const message = (error as any)?.message || 'Failed to delete room.';
      let userMessage = message;
      if (message.toLowerCase().includes('assigned to tasks')) {
        userMessage = i18n.t('admin_panel.delete_room_blocked_message');
      }
      setRemoveRoomModal(prev => ({ ...prev, isDeleting: false, error: userMessage }));
    }
  };


  // ✕ / Esc / tap outside (2026-10-17): asks to save only when something was changed.
  const roomCloseGuard = useCloseGuard({
    open: showAddRoomModal,
    snapshot: { tempRoomData, file: roomFile ? `${roomFile.name}:${roomFile.size}:${roomFile.lastModified}` : null, removePicture },
    save: () => (tempRoomData.id ? handleUpdateRoom() : handleCreateRoom()),
    discard: closeForm,
    saveDisabled: saving,
  });

  if (!current_family || !current_user) return null;
  return (
    <>
    <div className="mb-8">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-5">
        <h3 className="font-kid-heading text-2xl font-extrabold text-kid-text dark:text-kid-text-dark">{i18n.t('admin_panel.room_management')}</h3>
        <button 
          className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-kid-green hover:bg-kid-green-hover text-white shadow-[0_3px_0_0_#1F5C39] disabled:opacity-50"
          onClick={() => setShowAddRoomModal(true)}
        >
          + {i18n.t('admin_panel.add_new_room')}
        </button>
      </div>
      
      <div className="space-y-4">
        {current_family.rooms.map((room: any) => (
          <div key={room.id} className="bg-kid-card dark:bg-kid-card-dark rounded-kid-card border-2 border-kid-border dark:border-kid-border-dark p-4">
            <div className="flex justify-between items-center gap-3">
              <span className="flex items-center gap-3 min-w-0">
                <PictureTile src={room.image_url || null} label={room.name} size={56} tint="green" />
                <span className="text-lg font-medium text-kid-text dark:text-kid-text-dark break-words">{room.name}</span>
              </span>
              <div className="flex gap-2">
                <button 
                  className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-kid-card dark:bg-kid-card-dark border-2 border-kid-border-strong dark:border-kid-border-strong-dark text-kid-text dark:text-kid-text-dark disabled:opacity-50"
                  onClick={() => {
                    setTempRoomData(room);
                    setShowAddRoomModal(true);
                  }}
                >
                  {i18n.t('admin_panel.edit')}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
      {/* Room Modal */}
      {showAddRoomModal && (
        <EditorModal
          width="md"
          onRequestClose={roomCloseGuard.requestClose}
          title={tempRoomData.id ? i18n.t('admin_panel.room_modal_title_edit') : i18n.t('admin_panel.room_modal_title_add')}
        >
          <div className="p-6">
            <div className="mb-6 space-y-4">
              <div className="w-full">
                <label className="block text-base font-bold mb-1">{i18n.t('admin_panel.room_name_label_required')}</label>
                <input 
                  type="text" 
                  placeholder={i18n.t('admin_panel.room_name_placeholder')}
                  value={tempRoomData.name || ''}
                  onChange={(e) => setTempRoomData({ ...tempRoomData, name: e.target.value })} 
                  className="w-full min-h-[48px] rounded-kid-tile border-2 border-kid-border-strong dark:border-kid-border-strong-dark bg-kid-card dark:bg-kid-card-dark text-kid-text dark:text-kid-text-dark px-3 py-2 text-base focus:outline-none focus:border-kid-green" 
                />
              </div>
              <div className="w-full">
                <p className="block text-base font-bold mb-1">{i18n.t('admin_panel.room_picture_label')}</p>
                <p className="text-sm text-kid-text-muted dark:text-kid-text-muted-dark mb-2">{i18n.t('admin_panel.room_picture_hint')}</p>
                {(() => {
                  const shown = roomPreview || (!removePicture ? tempRoomData.image_url : null) || null;
                  return (
                    <div className="flex items-center gap-3 flex-wrap">
                      <PictureTile src={shown} label={tempRoomData.name || ''} size={96} tint="green" />
                      <div className="flex flex-col gap-2">
                        <label className="inline-flex items-center justify-center min-h-[44px] px-4 rounded-full font-bold text-base border-2 border-kid-border-strong dark:border-kid-border-strong-dark cursor-pointer">
                          {shown ? i18n.t('admin_panel.room_picture_change') : i18n.t('admin_panel.room_picture_add')}
                          <input
                            type="file"
                            accept="image/*"
                            className="sr-only"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              e.target.value = '';
                              if (!f) return;
                              setRoomFile(f);
                              setRoomPreview(URL.createObjectURL(f));
                              setRemovePicture(false);
                            }}
                          />
                        </label>
                        {shown && (
                          <button
                            type="button"
                            onClick={() => { setRoomFile(null); setRoomPreview(null); setRemovePicture(true); }}
                            className="min-h-[44px] px-4 rounded-full font-bold text-base text-red-800 dark:text-red-200 border-2 border-red-300 dark:border-red-800"
                          >
                            {i18n.t('pictures.remove_picture')}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
            <div className="flex gap-3">
              {tempRoomData.id && (
                <button
                  onClick={() => setRemoveRoomModal({ isOpen: true, roomId: String(tempRoomData.id), roomName: String(tempRoomData.name || '') })}
                  className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-transparent border-2 border-red-300 dark:border-red-800 text-red-800 dark:text-red-200 disabled:opacity-50 flex-1"
                >
                  {i18n.t('admin_panel.delete_room_btn')}
                </button>
              )}
              <button onClick={closeForm} disabled={saving} className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-kid-card dark:bg-kid-card-dark border-2 border-kid-border-strong dark:border-kid-border-strong-dark text-kid-text dark:text-kid-text-dark disabled:opacity-50 flex-1">{i18n.t('admin_panel.cancel_btn')}</button>
              <button onClick={tempRoomData.id ? handleUpdateRoom : handleCreateRoom} disabled={saving} className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-kid-green hover:bg-kid-green-hover text-white shadow-[0_3px_0_0_#1F5C39] disabled:opacity-50 flex-1">
                {tempRoomData.id ? i18n.t('admin_panel.save_changes_btn') : i18n.t('admin_panel.add_room_btn')}
              </button>
            </div>
          </div>
          {roomCloseGuard.dialog}
        </EditorModal>
      )}
      {/* Remove Room Confirmation Modal */}
      {removeRoomModal.isOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[10050] p-4">
          <div className="bg-kid-card dark:bg-kid-card-dark rounded-kid-tile max-w-md w-full mx-4 border-2 border-kid-border dark:border-kid-border-dark p-6">
            <h3 className="font-kid-heading text-xl font-extrabold text-kid-text dark:text-kid-text-dark mb-2">{i18n.t('admin_panel.delete_room_title')}</h3>
            {!removeRoomModal.error ? (
              <>
                <p className="text-kid-text dark:text-kid-text-dark mb-2">{i18n.t('admin_panel.delete_room_prompt_prefix')} "<strong>{removeRoomModal.roomName}</strong>"?</p>
                <p className="text-kid-text-muted dark:text-kid-text-muted-dark mb-4">{i18n.t('admin_panel.irreversible_note')}</p>
                <div className="flex gap-3">
                  <button
                    className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-kid-card dark:bg-kid-card-dark border-2 border-kid-border-strong dark:border-kid-border-strong-dark text-kid-text dark:text-kid-text-dark disabled:opacity-50 flex-1"
                    onClick={() => {
                      setRemoveRoomModal({ isOpen: false, roomId: '', roomName: '' });
                      setShowAddRoomModal(false);
                      setTempRoomData({});
                    }}
                    disabled={!!removeRoomModal.isDeleting}
                  >
                    {i18n.t('admin_panel.cancel_btn')}
                  </button>
                  <button
                    className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-transparent border-2 border-red-300 dark:border-red-800 text-red-800 dark:text-red-200 disabled:opacity-50 flex-1"
                    onClick={handleConfirmDeleteRoom}
                    disabled={!!removeRoomModal.isDeleting}
                  >
                    {i18n.t('admin_panel.delete_room_btn')}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-red-700 dark:text-red-300 mb-6">{removeRoomModal.error}</p>
                <div className="flex justify-end">
                  <button
                    className="inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-full font-bold text-base bg-kid-green hover:bg-kid-green-hover text-white shadow-[0_3px_0_0_#1F5C39] disabled:opacity-50"
                    onClick={() => {
                      setRemoveRoomModal({ isOpen: false, roomId: '', roomName: '' });
                      setShowAddRoomModal(false);
                      setTempRoomData({});
                    }}
                  >
                    {i18n.t('common.ok')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default RoomsTab;
